import React from 'react';
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';

type MarkdownContentProps = {
  children: string;
  options?: MarkdownToJSX.Options;
};

/**
 * A wrapper around markdown-to-jsx for displaying markdown content
 * with sensible defaults and customization control.
 *
 * Provides preset options for common use cases:
 * - Anchor tags open in new tabs with security attributes (target="_blank", rel="noopener noreferrer")
 *
 * Additional options can be passed to customize rendering behavior,
 * with user-provided options taking precedence over defaults.
 *
 * Security props are preserved even when users override anchor tag components.
 */
export const MarkdownContent = ({ children, options = {} }: MarkdownContentProps) => {
  // Handle edge cases: empty strings or invalid input
  if (!children || typeof children !== 'string') {
    return null;
  }

  const defaultOptions: MarkdownToJSX.Options = {
    overrides: {
      a: {
        component: 'a',
        props: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
    },
  };

  // Merge user-provided options with defaults, with user options taking precedence
  // Deep merge override entries to preserve security props when users override components
  const mergedOverrides: MarkdownToJSX.Options['overrides'] = {
    ...defaultOptions.overrides,
    ...options.overrides,
  };

  // Deep merge anchor tag override to preserve security props even if user overrides component
  const defaultAnchorOverride = defaultOptions.overrides?.a;
  const userAnchorOverride = options.overrides?.a;

  if (userAnchorOverride && defaultAnchorOverride) {
    // Type guard: check if both overrides have the expected structure
    if (
      typeof defaultAnchorOverride === 'object' &&
      'props' in defaultAnchorOverride &&
      typeof userAnchorOverride === 'object'
    ) {
      mergedOverrides.a = {
        ...defaultAnchorOverride,
        ...userAnchorOverride,
        props: {
          ...(defaultAnchorOverride.props || {}),
          ...(('props' in userAnchorOverride && userAnchorOverride.props) || {}),
        },
      } as typeof defaultAnchorOverride;
    }
  }

  const mergedOptions: MarkdownToJSX.Options = {
    ...defaultOptions,
    ...options,
    overrides: mergedOverrides,
  };

  return <Markdown options={mergedOptions}>{children}</Markdown>;
};
